import { useEffect, useCallback, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { HubConnectionBuilder } from '@microsoft/signalr'
import axios from 'axios'
import { 
  setNotifications, 
  addNotification, 
  markNotificationsAsRead, 
  setConnectionStatus 
} from '../../redux/notifications'

export const useNotificationHub = ({ 
  operatorId, 
  operatorUserId,
  hubUrl,
  authToken
}) => {
  const dispatch = useDispatch()
  const { notifications, isConnected, error } = useSelector(state => state.notifications)
  const connectionRef = useRef(null)
  const reconnectTimerRef = useRef(null)

  // Ensure we always provide a valid token to SignalR
  const refreshAccessToken = useCallback(async () => {
    try {
      const url = `${process.env.REACT_APP_ACCOUNT_API_URL}/User/RefreshAccessToken`
      const res = await axios.post(url, {
        accessToken: '',
        refreshTokenId: '',
        isAccessTokenCookiePersistant: false,
        isRefreshTokenCookiePersistant: false
      }, { withCredentials: true })
      const newJwt = res?.data?.jwt || res?.data?.accessToken
      if (newJwt) {
        localStorage.setItem('accessToken', newJwt)
        return newJwt
      }
    } catch (_) {
      // ignore; caller will handle auth failures
    }
    return ''
  }, [])

  const isTokenExpiring = (token) => {
    try {
      const [, payload] = token.split('.')
      const { exp } = JSON.parse(atob(payload))
      const now = Math.floor(Date.now() / 1000)
      return !exp || exp - now < 30 // refresh if expires in <30s
    } catch {
      return true
    }
  }

  const getValidAccessToken = useCallback(async () => {
    const current = authToken || localStorage.getItem('accessToken') || ''
    if (!current || isTokenExpiring(current)) {
      const refreshed = await refreshAccessToken()
      return refreshed || current || ''
    }
    return current
  }, [authToken, refreshAccessToken])

  // Fetch notifications via REST API
  const fetchNotifications = useCallback(async () => {
    try {
      const apiBase = process.env.REACT_APP_ACCOUNT_API_URL
      const url = `${apiBase}/notification/list`
      const params = {}
      if (operatorId) params.operatorId = parseInt(operatorId, 10)
      if (operatorUserId) params.operatorUserId = parseInt(operatorUserId, 10)

      const { data } = await axios.get(url, { params, withCredentials: true })

      // Accept either { success, data } shape or a raw array
      if (data && Array.isArray(data)) {
        dispatch(setNotifications(data))
      } else if (data && data.success) {
        dispatch(setNotifications(data.data || []))
      } else {
        dispatch(setNotifications([]))
        if (data && data.error) {
          dispatch(setConnectionStatus({ isConnected: true, error: data.error }))
        }
      }
    } catch (e) {
      dispatch(setConnectionStatus({ 
        isConnected: true, 
        error: e instanceof Error ? e.message : 'Failed to load notifications' 
      }))
    }
  }, [operatorId, operatorUserId, dispatch])

  // Initialize connection
  const startConnection = useCallback(async () => {
    try {
      const urlToUse = hubUrl || process.env.REACT_APP_NOTIFICATION_HUB_URL
      const hubConnection = new HubConnectionBuilder()
        .withUrl(urlToUse, {
          accessTokenFactory: async () => await getValidAccessToken(),
          withCredentials: true
        })
        // Customize reconnect delays: immediate, 2s, 10s, 30s, 60s
        .withAutomaticReconnect([0, 2000, 10000, 30000, 60000])
        .build()

      // Keep-alive tuning to reduce idle disconnects
      hubConnection.serverTimeoutInMilliseconds = 120000 // 2 minutes
      hubConnection.keepAliveIntervalInMilliseconds = 15000 // 15 seconds

      // Handle incoming notifications
      hubConnection.on('ReceiveNotification', notification => {        
        dispatch(addNotification(notification))
      })

      // Handle connection events
      hubConnection.onclose(error => {
        dispatch(setConnectionStatus({ 
          isConnected: false, 
          error: error?.message || 'Connection closed' 
        }))
        // After automatic reconnect attempts exhaust, try again on a backoff
        if (!reconnectTimerRef.current) {
          reconnectTimerRef.current = setTimeout(() => {
            reconnectTimerRef.current = null
            // Only restart if still not connected and we still own this instance
            if (!connectionRef.current) return
            startConnection()
          }, 30000) // 30s manual retry
        }
      })

      hubConnection.onreconnecting(() => {
        dispatch(setConnectionStatus({ 
          isConnected: false, 
          error: 'Reconnecting...' 
        }))
      })

      hubConnection.onreconnected(async () => {
        dispatch(setConnectionStatus({ 
          isConnected: true, 
          error: null 
        }))
        // Re-fetch notifications after reconnection via API
        fetchNotifications()
      })

      await hubConnection.start()

      // Store connection reference immediately after successful start
      connectionRef.current = hubConnection

      dispatch(setConnectionStatus({ isConnected: true, error: null }))

      // Load existing notifications via API (do not pass undefined args)
      fetchNotifications()

    } catch (err) {
      dispatch(setConnectionStatus({ 
        isConnected: false, 
        error: err instanceof Error ? err.message : 'Failed to connect' 
      }))
    }
  }, [operatorId, operatorUserId, hubUrl, authToken, dispatch, fetchNotifications])

  // Disconnect
  const stopConnection = useCallback(async () => {
    if (connectionRef.current) {
      await connectionRef.current.stop()
      connectionRef.current = null
      dispatch(setConnectionStatus({ isConnected: false, error: null }))
    }
  }, [dispatch])

  // Mark notifications as read
  const markAsRead = useCallback(async notificationIds => {
    try {
      await axios.post(`${process.env.REACT_APP_ACCOUNT_API_URL}/notification/mark-as-read`, notificationIds, {
        withCredentials: true
      })
      dispatch(markNotificationsAsRead(notificationIds))
    } catch (err) {
      dispatch(setConnectionStatus({ 
        isConnected: false, 
        error: err instanceof Error ? err.message : 'Failed to mark as read' 
      }))
    }
  }, [dispatch])

  // Effect to manage connection lifecycle
  useEffect(() => {
    startConnection()
    return () => {
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current)
        reconnectTimerRef.current = null
      }
      stopConnection()
      connectionRef.current = null
    }
  }, [startConnection, stopConnection])

  return {
    notifications,
    isConnected,
    error,
    markAsRead,
    startConnection,
    stopConnection
  }
}

// Centralized notification action handler
export const createNotificationActionHandler = ({ history }) => (notification, action) => {
  if ((action.type === "RouteToURL" || action.type === 0) && action.action.startsWith('/')) { // RouteToURL
    // Parse the action URL to extract path and query parameters
    // This allows notifications to pass query parameters like "/Profiles?reviewId=1"
    const url = new URL(action.action, window.location.origin)
    const path = url.pathname
    const search = url.search
    
    if (path === '/notifications' || path === '/Home') {
      // For local routes, use history.push with search params
      history.push({ pathname: path, search })
    } else {
      // For iframe routes, we need to pass the query parameters through the routing system
      // The IFramedContent component will handle appending query parameters to iframe sources
      if (search) {
        // For routes with query parameters, we'll use a special navigation approach
        // that preserves the query parameters for the iframe
        const fullPath = path + search
        window.top.location.assign(fullPath)
      } else {
        // For routes without query parameters, use the existing approach
        window.top.location.assign(action.action)
      }
    }
  } else {
    // Implement other actions as needed
    console.log('Action:', action, 'on notification:', notification)
  }
}

export default useNotificationHub

// Simple hook to access notifications state from Redux
export const useNotifications = () => {
  const { notifications, isConnected, error } = useSelector(state => state.notifications)
  return { notifications, isConnected, error }
} 